import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Plus, Printer, RefreshCw, Trash2 } from 'lucide-react'
import { notifications } from '@mantine/notifications'
import { useClientProject } from '@/contexts'
import { normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { DocumentSkeleton } from '@/components/route-shell/skeletons/DocumentSkeleton'
import { SemanticReportDocument } from '@/components/reports/SemanticReportDocument'
import { ReportPrintPreview } from '@/components/reports/ReportPrintPreview'
import {
  deleteBlockAt,
  hasManualChanges,
  insertBlock,
  mergeSectionIntoPrevious,
  moveBlock,
  startSectionAt,
  withEditedBlock,
  withSectionTitle,
} from '@/components/reports/reportDocumentEdits'
import type { BlockPosition } from '@/components/reports/reportDocumentEdits'
import {
  deleteReport,
  executeReportRun,
  getReport,
  getReportRun,
  listReportRuns,
  listReports,
  saveReportRun,
} from '@/services/reportService'
import type { ReportRun } from '@/services/reportService'
import { chatbotService } from '@/services'
import type { ChatMessage } from '@/types'
import { isReportDocument } from '@chart-core'
import type { ReportDocument } from '@chart-core'
import { ReportPromptHero } from './-reports/ReportPromptHero'
import { ReportGenerationView } from './-reports/ReportGenerationView'
import type { GenerationTerminal } from './-reports/ReportGenerationView'
import { ReportRefineBar } from './-reports/ReportRefineBar'
import '@/styles/pages.css'
import '@/styles/custom-dashboards.css'
import '@/styles/reports.css'

interface ReportsSearch {
  /** id of the saved report (short or dashed form); omitted = nothing selected yet */
  report?: string
  /** id of the run (document) to render; omitted = auto-select the newest */
  run?: string
  /** opens the paginated print preview over the selected run */
  print?: boolean
}

// DB-backed narrative reports (narrative_report rows + narrative_report_run documents, served by
// /api/projects/{uuid}/reports). Prompt-first like custom-dashboards: the empty state is a
// "What should this report cover?" hero, generating streams the report agent's SSE progress, and
// a rendered run carries a floating refine bar pinned to the report on screen. A run's document
// is a frozen snapshot — prose is editable in place, figures never re-query — and the print
// preview paginates it with Paged.js. No `_new`-style chat-builder variant in v1: the refine bar
// reuses the same agent session, and reports are consumed as documents rather than arranged live.
//   /organization/reports?projectId=…[&report=<id>][&run=<id>][&print=1]
export const Route = createFileRoute('/_app/organization/reports')({
  head: () => ({ meta: [{ title: 'Reports · AERRIS' }] }),
  component: ReportsPage,
  validateSearch: (search: Record<string, unknown>): ReportsSearch => ({
    report: typeof search.report === 'string' ? search.report : undefined,
    run: typeof search.run === 'string' ? search.run : undefined,
    print: search.print === true || search.print === '1' || search.print === 1 || undefined,
  }),
})

const panelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '200px',
  color: 'var(--text-muted, #6b7280)',
  textAlign: 'center',
  backgroundColor: 'var(--container-bg, #ffffff)',
  border: '1px solid var(--border-color, #e5e7eb)',
  borderRadius: 'var(--radius-lg)',
  padding: '2rem',
}

function Panel({ title, body, action }: { title: string; body?: string; action?: React.ReactNode }) {
  return (
    <div style={panelStyle}>
      <h2 style={{ fontSize: '1.125rem', margin: 0 }}>{title}</h2>
      {body && <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem' }}>{body}</p>}
      {action && <div style={{ marginTop: '1rem' }}>{action}</div>}
    </div>
  )
}

const navStyle: CSSProperties = {
  width: '280px',
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '1rem',
  borderRight: '1px solid var(--border-color, #e5e7eb)',
  backgroundColor: 'var(--container-bg, #ffffff)',
  overflowY: 'auto',
}

const createButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.5rem',
  width: '100%',
  padding: '0.6rem 0.75rem',
  cursor: 'pointer',
  backgroundColor: 'var(--accent-ink, #16324f)',
  color: '#ffffff',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 600,
}

const navItemStyle = (active: boolean): CSSProperties => ({
  width: '100%',
  textAlign: 'left',
  padding: '0.6rem 0.75rem',
  cursor: 'pointer',
  backgroundColor: active ? 'var(--surface-secondary, #f3f4f6)' : 'transparent',
  border: `1px solid ${active ? 'var(--border-color, #e5e7eb)' : 'transparent'}`,
  borderRadius: 'var(--radius-md)',
})

/** One in-flight agent run: what was asked, what the agent has said so far, how it ended. */
interface GenerationState {
  prompt: string
  /** Progress texts in arrival order — seeded synthetically, then the SSE THINKING messages. */
  steps: string[]
  /** Name of the report being built: known up front on a refine, learned on the first save. */
  title: string | null
  /** Set when the run ended without a report (failure, or the agent replied without saving). */
  terminal: GenerationTerminal | null
}

function ReportsPage() {
  const { projectId, selectedProject } = useClientProject()
  const { report: reportParam, run: runParam, print } = Route.useSearch()
  // Params arrive in either id form (short from new links, dashed from old ones); the API calls
  // below need the UUIDs.
  const reportUuid = normalizeEntityId(reportParam)
  const runUuid = normalizeEntityId(runParam)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [generation, setGeneration] = useState<GenerationState | null>(null)

  const closeConnectionRef = useRef<(() => void) | null>(null)
  // The report the CURRENT agent run is building, plus the run uuid once one completes. Seeded
  // from the refine target (undefined for a fresh build) and overwritten by mid-stream REPORT
  // parts, so a retry — or a second save in the same turn — updates the report the agent already
  // created instead of duplicating it.
  const targetReportRef = useRef<{ report: string | undefined; run: string | undefined }>({
    report: undefined,
    run: undefined,
  })
  // One agent conversation per report being worked on: consecutive refines of the same report
  // share a session (the agent keeps its context); switching targets starts fresh.
  const sessionRef = useRef<{ uuid: string; target: string | undefined } | null>(null)

  useEffect(() => {
    return () => closeConnectionRef.current?.()
  }, [])

  const list = useQuery({
    queryKey: ['reports', projectId],
    queryFn: () => listReports(projectId!),
    enabled: !!projectId,
  })
  const report = useQuery({
    queryKey: ['report', projectId, reportUuid],
    queryFn: () => getReport(projectId!, reportUuid!),
    enabled: !!projectId && !!reportUuid,
  })
  const runs = useQuery({
    queryKey: ['report-runs', projectId, reportUuid],
    queryFn: () => listReportRuns(projectId!, reportUuid!),
    enabled: !!projectId && !!reportUuid,
  })
  const run = useQuery({
    queryKey: ['report-run', projectId, reportUuid, runUuid],
    queryFn: () => getReportRun(projectId!, reportUuid!, runUuid!),
    enabled: !!projectId && !!reportUuid && !!runUuid,
  })

  // A report without an explicit ?run= lands on its newest run (runs arrive newest-first).
  useEffect(() => {
    if (!reportUuid || runUuid || !runs.data || runs.data.length === 0) return
    navigate({
      to: '/organization/reports',
      search: (prev) => ({ ...prev, run: toShortEntityId(runs.data[0].uuid) }),
      replace: true,
    })
  }, [reportUuid, runUuid, runs.data, navigate])

  const stopGeneration = () => {
    closeConnectionRef.current?.()
    closeConnectionRef.current = null
    setGeneration(null)
  }

  const invalidateReport = (uuid: string) => {
    queryClient.invalidateQueries({ queryKey: ['reports', projectId] })
    queryClient.invalidateQueries({ queryKey: ['report', projectId, uuid] })
    queryClient.invalidateQueries({ queryKey: ['report-runs', projectId, uuid] })
  }

  const handleMessage = (message: ChatMessage) => {
    if (message.type === 'THINKING') {
      const text = message.message
      if (!text) return
      setGeneration((prev) =>
        prev && prev.steps[prev.steps.length - 1] !== text
          ? { ...prev, steps: [...prev.steps, text] }
          : prev,
      )
    } else if (message.type === 'REPORT' && message.report) {
      const { uuid, name, runUuid: freshRun } = message.report
      targetReportRef.current = { report: uuid, run: freshRun ?? targetReportRef.current.run }
      if (sessionRef.current) sessionRef.current.target = uuid
      setGeneration((prev) => (prev ? { ...prev, title: name || prev.title } : prev))
      // The saved definition / run set changed server-side; drop cached copies.
      invalidateReport(uuid)
    } else if (message.type === 'ANSWER') {
      const finished = targetReportRef.current
      closeConnectionRef.current = null
      if (finished.report) {
        setGeneration(null)
        navigate({
          to: '/organization/reports',
          search: (prev) => ({
            ...prev,
            report: toShortEntityId(finished.report!),
            // Land straight on the run the agent just produced; otherwise let auto-select pick
            // the newest once the runs list refetches.
            run: finished.run ? toShortEntityId(finished.run) : undefined,
          }),
          replace: true,
        })
      } else {
        // The agent finished without saving anything — usually a clarifying question. Show its
        // reply in the status card rather than silently bouncing back to the hero.
        const text = message.message || 'The report was not generated. Try rephrasing your prompt.'
        setGeneration((prev) => (prev ? { ...prev, terminal: { kind: 'notice', text } } : prev))
      }
    } else if (message.type === 'QUESTION' && message.question) {
      // The agent asked a clarifying question and ended its turn (the stream is closed): show
      // the question with its option chips; the answer continues the same conversation.
      const { question, options } = message.question
      closeConnectionRef.current = null
      setGeneration((prev) =>
        prev ? { ...prev, terminal: { kind: 'question', text: question, options } } : prev,
      )
    } else if (message.type === 'ERROR') {
      closeConnectionRef.current = null
      setGeneration((prev) =>
        prev
          ? { ...prev, terminal: { kind: 'error', text: message.message ?? 'Something went wrong.' } }
          : prev,
      )
    }
  }

  /** Kick off an agent run: `target` = the report to refine, undefined to build a new one. */
  const startGeneration = (prompt: string, target: string | undefined, title: string | null) => {
    if (!projectId) return
    closeConnectionRef.current?.()
    targetReportRef.current = { report: target, run: undefined }
    if (!sessionRef.current || sessionRef.current.target !== target) {
      sessionRef.current = { uuid: crypto.randomUUID(), target }
    }
    setGeneration({ prompt, steps: ['Reading your prompt'], title, terminal: null })
    closeConnectionRef.current = chatbotService.sendReportMessage(
      {
        message: prompt,
        chatSessionUuid: sessionRef.current.uuid,
        projectUuid: projectId,
        reportUuid: target,
      },
      handleMessage,
    )
  }

  const generateNew = (prompt: string) => startGeneration(prompt, undefined, null)
  const refine = (prompt: string) => startGeneration(prompt, reportUuid, report.data?.name ?? null)
  const retry = () => {
    if (!generation) return
    startGeneration(generation.prompt, targetReportRef.current.report, generation.title)
  }
  /** Answer a clarifying question (or reply to a notice): same session and target, steps kept. */
  const replyToAgent = (text: string) => {
    if (!projectId || !sessionRef.current) return
    closeConnectionRef.current?.()
    setGeneration((prev) =>
      prev ? { ...prev, steps: [...prev.steps, `You: ${text}`], terminal: null } : prev,
    )
    closeConnectionRef.current = chatbotService.sendReportMessage(
      {
        message: text,
        chatSessionUuid: sessionRef.current.uuid,
        projectUuid: projectId,
        reportUuid: targetReportRef.current.report,
      },
      handleMessage,
    )
  }

  const open = (uuid: string | undefined, runId?: string) => {
    stopGeneration()
    navigate({
      to: '/organization/reports',
      // Preserve the project selection (kept in URL search by ClientProjectContext).
      search: (prev) => ({
        ...prev,
        report: uuid ? toShortEntityId(uuid) : undefined,
        run: runId ? toShortEntityId(runId) : undefined,
        print: undefined,
      }),
    })
  }
  const createNew = () => open(undefined)

  // Direct document edits (prose changes AND block repositioning): optimistic swap of the cached
  // run document, rolled back on failure.
  const saveBlock = useMutation({
    mutationFn: ({ document }: { document: ReportDocument }) =>
      saveReportRun(projectId!, reportUuid!, runUuid!, document),
    onMutate: async ({ document }) => {
      const key = ['report-run', projectId, reportUuid, runUuid]
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<ReportRun>(key)
      if (previous) queryClient.setQueryData<ReportRun>(key, { ...previous, document })
      return { previous, key }
    },
    onError: (error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(context.key, context.previous)
      notifications.show({
        color: 'red',
        title: "Couldn't save your edit",
        message: error instanceof Error ? error.message : 'Something went wrong.',
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['report-run', projectId, reportUuid, runUuid] })
    },
  })

  const onSaveBlock = (sectionId: string, blockId: string, markdown: string) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    saveBlock.mutate({ document: withEditedBlock(current, sectionId, blockId, markdown) })
  }

  // Drag-to-reposition: same run-level persistence as prose edits (the definition keeps its own
  // order — a durable reorder is a refine-bar ask, which edits the definition itself).
  const onMoveBlock = (from: BlockPosition, to: BlockPosition) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    const moved = moveBlock(current, from, to)
    if (moved !== current) saveBlock.mutate({ document: moved })
  }

  // A manually inserted prose block is born `edited` — it exists only on this run, and the badge
  // plus the re-run warning already say exactly that.
  const onInsertBlock = (at: BlockPosition, markdown: string) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    saveBlock.mutate({
      document: insertBlock(current, at, { type: 'markdown', id: crypto.randomUUID(), markdown, edited: true }),
    })
  }

  // "+ Heading" starts a new section at the gap (a heading IS a section title); deleting one
  // merges the section back into its predecessor. Both are run-level, like every edit here.
  const onInsertHeading = (at: BlockPosition, title: string) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    saveBlock.mutate({ document: startSectionAt(current, at, title, crypto.randomUUID()) })
  }

  const onInsertPageBreak = (at: BlockPosition) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    saveBlock.mutate({ document: insertBlock(current, at, { type: 'pageBreak' }) })
  }

  const onDeleteBlock = (at: BlockPosition) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    const deleted = deleteBlockAt(current, at)
    if (deleted !== current) saveBlock.mutate({ document: deleted })
  }

  const onSaveSectionTitle = (sectionId: string, title: string) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    saveBlock.mutate({ document: withSectionTitle(current, sectionId, title) })
  }

  const onDeleteSectionTitle = (sectionId: string) => {
    const current = run.data?.document
    if (!current || !isReportDocument(current)) return
    const merged = mergeSectionIntoPrevious(current, sectionId)
    if (merged !== current) saveBlock.mutate({ document: merged })
  }

  // "Run again": synchronous server-side re-execution (queries + narrative), tens of seconds.
  const rerun = useMutation({
    mutationFn: () => executeReportRun(projectId!, reportUuid!),
    onSuccess: (fresh) => {
      invalidateReport(reportUuid!)
      open(reportUuid, fresh.uuid)
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: "Couldn't run the report",
        message: error instanceof Error ? error.message : 'Something went wrong.',
      })
    },
  })

  const runAgain = () => {
    const doc = run.data?.document
    // Inserted/repositioned blocks and renamed titles are detected by comparing the document
    // against the definition (ids and titles are copied verbatim by the run pipeline) — nothing
    // extra is persisted for it.
    if (
      doc &&
      isReportDocument(doc) &&
      hasManualChanges(doc, report.data?.definition) &&
      !window.confirm(
        'Your manual changes apply only to this run and will not appear in the new run. Run again?',
      )
    ) {
      return
    }
    rerun.mutate()
  }

  const removeReport = useMutation({
    mutationFn: (uuid: string) => deleteReport(projectId!, uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reports', projectId] })
      open(undefined)
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: "Couldn't delete the report",
        message: error instanceof Error ? error.message : 'Something went wrong.',
      })
    },
  })

  const remove = (uuid: string, name: string | null) => {
    if (window.confirm(`Delete “${name ?? 'Untitled report'}” and all its runs?`)) {
      removeReport.mutate(uuid)
    }
  }

  if (!projectId) {
    return (
      <div style={{ padding: '1.5rem' }}>
        <Panel title="Select a Client and Project" body="Choose a client and project to view its reports" />
      </div>
    )
  }

  const generationTitle = generation?.title ?? 'New report'
  const document = run.data && isReportDocument(run.data.document) ? run.data.document : null
  const showRefineBar = !generation && !!reportUuid && !!document

  return (
    <div className="custom-dash-page">
      <aside style={navStyle}>
        <button type="button" onClick={createNew} style={createButtonStyle}>
          <Plus size={16} />
          Create Report
        </button>
        {list.isPending ? (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted, #6b7280)' }}>Loading reports…</span>
        ) : list.isError ? (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted, #6b7280)' }}>Couldn't load reports</span>
        ) : list.data.length === 0 ? (
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted, #6b7280)' }}>No saved reports yet</span>
        ) : (
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {list.data.map((item) => (
              <div key={item.uuid}>
                <button
                  type="button"
                  onClick={() => open(item.uuid)}
                  style={navItemStyle(item.uuid === reportUuid)}
                >
                  <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{ flex: 1 }}>{item.name ?? 'Untitled report'}</span>
                    {item.uuid === reportUuid && (
                      <Trash2
                        size={14}
                        aria-label="Delete this report"
                        style={{ color: 'var(--text-muted, #6b7280)' }}
                        onClick={(e) => {
                          e.stopPropagation()
                          remove(item.uuid, item.name)
                        }}
                      />
                    )}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #6b7280)' }}>
                    updated {new Date(item.updatedAt).toLocaleDateString()}
                    {` · ${item.runCount} run${item.runCount === 1 ? '' : 's'}`}
                  </span>
                </button>
                {item.uuid === reportUuid && runs.data && runs.data.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', marginTop: '0.25rem' }}>
                    {runs.data.map((r) => (
                      <button
                        key={r.uuid}
                        type="button"
                        className={r.uuid === runUuid ? 'report-run-item report-run-item--active' : 'report-run-item'}
                        onClick={() => open(item.uuid, r.uuid)}
                      >
                        {new Date(r.createdAt).toLocaleString()}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        )}
      </aside>
      <main className={showRefineBar ? 'custom-dash-main custom-dash-main--with-bar' : 'custom-dash-main'}>
        <div className="custom-dash-scroll">
          {generation ? (
            <>
              <header className="custom-dash-header">
                <div>
                  <h1>{generationTitle}</h1>
                  <p className="custom-dash-header__meta">Building from: “{generation.prompt}”</p>
                </div>
              </header>
              <ReportGenerationView
                steps={generation.steps}
                terminal={generation.terminal}
                onRetry={retry}
                onCancel={stopGeneration}
                onReply={replyToAgent}
              />
            </>
          ) : !reportUuid ? (
            <ReportPromptHero contractCount={selectedProject?.contractCount} onGenerate={generateNew} />
          ) : report.isPending || runs.isPending ? (
            <DocumentSkeleton />
          ) : report.isError ? (
            <Panel title="Couldn't load this report" body={String(report.error)} />
          ) : !report.data ? (
            <Panel title="Report not found" body="It may have been deleted, or it belongs to another project." />
          ) : runs.data && runs.data.length === 0 ? (
            <Panel
              title="No runs yet"
              body="This report has a definition but has never been run against your data."
              action={
                <button type="button" style={createButtonStyle} onClick={runAgain} disabled={rerun.isPending}>
                  {rerun.isPending ? 'Running…' : 'Run report'}
                </button>
              }
            />
          ) : !runUuid || run.isPending ? (
            <DocumentSkeleton />
          ) : run.isError ? (
            <Panel title="Couldn't load this run" body={String(run.error)} />
          ) : !run.data ? (
            <Panel title="Run not found" body="It may have been deleted, or it belongs to another report." />
          ) : !document ? (
            <Panel title="This run can't be rendered" body="Its saved document isn't a recognised report shape." />
          ) : (
            <>
              <header className="custom-dash-header">
                <div>
                  <h1>{report.data.name ?? 'Untitled report'}</h1>
                  <p className="custom-dash-header__meta">
                    run {new Date(document.generatedAt).toLocaleString()}
                  </p>
                </div>
                <div className="custom-dash-header__actions">
                  <button type="button" onClick={runAgain} disabled={rerun.isPending}>
                    <RefreshCw size={14} aria-hidden="true" />
                    {rerun.isPending ? 'Running…' : 'Run again'}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        to: '/organization/reports',
                        search: (prev) => ({ ...prev, print: true }),
                      })
                    }
                  >
                    <Printer size={14} aria-hidden="true" />
                    Print / Export PDF
                  </button>
                </div>
              </header>
              <SemanticReportDocument
                document={document}
                onSaveBlock={onSaveBlock}
                onMoveBlock={onMoveBlock}
                onInsertBlock={onInsertBlock}
                onInsertHeading={onInsertHeading}
                onInsertPageBreak={onInsertPageBreak}
                onDeleteBlock={onDeleteBlock}
                onSaveSectionTitle={onSaveSectionTitle}
                onDeleteSectionTitle={onDeleteSectionTitle}
                saving={saveBlock.isPending}
              />
            </>
          )}
        </div>
        {showRefineBar && <ReportRefineBar onSend={refine} />}
      </main>
      {print && document && (
        <ReportPrintPreview
          document={document}
          onClose={() =>
            navigate({
              to: '/organization/reports',
              search: (prev) => ({ ...prev, print: undefined }),
            })
          }
        />
      )}
    </div>
  )
}
